import { useDataFetchingAuth } from "@inmagik/react-crud/dist/crud/utils"
import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from "@tanstack/react-query"

type Params = Record<string, string | number | string[] | number[] | null | undefined>
type TGetOptions<TResponse> = {
  query?: Params
  headers?: Record<string, string>
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
  responseType?: "json" | "blob" | "text" | "raw"
  reactQuery?: Omit<UseQueryOptions<TResponse>, "queryFn" | "queryKey"> & {
    queryPrefix?: unknown[]
    queryKey?: UseQueryOptions<TResponse>["queryKey"]
  }
}

export function useData<T>(apiPath: string, options?: TGetOptions<T>) {
  const optQuery = options?.query || {}
  const optHeaders = options?.headers || {}
  const optMethod = options?.method || "GET"
  const optResponseType = options?.responseType || "json"
  const optReactQueryOptions = options?.reactQuery || {}

  const paramsString = normalizeParams(optQuery).toString()
  const apiUrl = `${apiPath}/?${paramsString}`
  const authHeaders = useDataFetchingAuth()
  const headers = { ...authHeaders, ...optHeaders }

  const keyPrefix = optReactQueryOptions.queryPrefix || [apiPath]

  return useQuery<T>({
    queryKey: [
      ...keyPrefix,
      { query: optQuery, headers: optHeaders, method: optMethod, responseType: optResponseType },
    ],
    queryFn: ({ signal }) => fetchApi(apiUrl, { signal, headers, method: optMethod, responseType: optResponseType }),
    ...optReactQueryOptions,
  })
}

function normalizeParams(params: Params) {
  const result = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== null && item !== undefined) {
          result.append(key, item.toString())
        }
      }
    } else {
      if (value !== null && value !== undefined) {
        result.append(key, value.toString())
      }
    }
  }
  return result
}

async function fetchApi(
  url: string,
  options: {
    method?: string
    signal?: AbortSignal
    headers: Record<string, string>
    body?: Blob | BufferSource | FormData | URLSearchParams | string
    responseType?: "json" | "text" | "blob" | "raw"
  }
) {
  const res = await fetch(url, options)
  if (!res.ok) {
    let data = {}
    try {
      data = await res.json()
    } catch (e) {
      // Ignore error
    }
    throw new ApiError(res, data)
  }
  const responseType = options.responseType || "json"
  if (responseType === "json") {
    return await res.json()
  } else if (responseType === "text") {
    return await res.text()
  } else if (responseType === "blob") {
    return await res.blob()
  } else {
    return res
  }
}

class ApiError extends Error {
  response: Response
  data: any

  constructor(response: Response, data: any) {
    super(response.statusText)
    this.response = response
    this.data = data
  }
}

export type EncoderFn<T> = (record: T) => FormData | string
export type Encoder<T> = "json" | "formdata" | EncoderFn<T>


type TActionOptions<TPayload, TResponse> = {
  query?: Params
  headers?: Record<string, string>
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
  responseType?: "json" | "blob" | "text" | "raw"
  encode?: Encoder<TPayload>
  reactQuery?: Omit<UseMutationOptions<TResponse, ApiError, TPayload>, "mutationFn">
}

export function useAction<TResult, TData = TResult>(apiPath: string, options?: TActionOptions<TData, TResult>) {
  const optQuery = options?.query || {}
  const optHeaders = options?.headers || {}
  const optMethod = options?.method || "POST"
  const optResponseType = options?.responseType || "json"
  const optEncode = options?.encode || "json"
  const optReactQueryOptions = options?.reactQuery || {}

  const paramsString = normalizeParams(optQuery).toString()
  const authHeaders = useDataFetchingAuth()
  const headers = { ...authHeaders, ...optHeaders }
  if (optEncode === "json" && headers["Content-Type"] === undefined) {
    headers["Content-Type"] = "application/json"
  }
  const apiUrl = `${apiPath}/?${paramsString}`

  const encode = createEncoder(optEncode)

  return useMutation<TResult, ApiError, TData>({
    mutationFn: (record: TData) => {
      return fetchApi(apiUrl, {
        method: optMethod,
        headers,
        body: record ? encode(record) : undefined,
        responseType: optResponseType,
      })
    },
    ...optReactQueryOptions,
  })
}

export function data2formdata(data: any) {
  const fd = new FormData()
  if (data instanceof File) {
    fd.append("", data)
  } else if (Array.isArray(data)) {
    for (let i = 0; i < data.length; i++) {
      const itemFD = data2formdata(data[i])
      for (const [key, value] of itemFD.entries()) {
        fd.append(`[${i}]${key}`, value)
      }
    }
  } else if (typeof data === "object" && data !== null) {
    for (const [outerKey, value] of Object.entries(data)) {
      const itemFD = data2formdata(value)
      for (const [key, value] of itemFD.entries()) {
        if (key === "") {
          fd.append(`${outerKey}`, value)
        } else if (key[0] === "[") {
          fd.append(`${outerKey}${key}`, value)
        } else {
          fd.append(`${outerKey}.${key}`, value)
        }
      }
    }
  } else {
    fd.append("", data?.toString() ?? "")
  }
  return fd
}

export function createEncoder<T>(encode: Encoder<T>): EncoderFn<T> {
  if (encode === "json") {
    return JSON.stringify
  }
  if (encode === "formdata") {
    return data2formdata
  }
  return encode
}
